import React, { useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { Link } from '@tanstack/react-router';
import { MappingType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/mapping.pb';
import dayjs from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { breakpoints, useBreakpoint, useMatchMedia } from '@frontend/responsiveness';
import { useLocationTimezone } from '@frontend/schedule-helpers';
import { titleCase } from '@frontend/string';
import { isEmpty } from '@frontend/validation';
import { theme } from '@frontend/theme';
import { Avatar, Chip, Text, useAlert, numeric } from '@frontend/design-system';
import { AppointmentTypeChip } from '../../../../../../components/appointment-type-chip';
import { DATE_DISPLAY_FORMAT } from '../../../../../../utils/date-helpers';
import { useGetScheduleRequest } from '../hooks/useGetScheduleRequest';
import { getCustomFieldData, getFormattedScheduleRequestAddress, getScheduleRequestBirthDate } from '../utils';
import { ScheduleRequestDetailsProperty } from './components/ScheduleRequestDetailsProperty';
import { ScheduleRequestDetailsSpinnerLoader } from './components/ScheduleRequestDetailsSpinnerLoader';

interface ScheduleRequestDetailsProps {
  scheduleRequestId: string;
  requestVersion: string;
  locationId: string;
  isPaymentsEnabled: boolean;
}

export const ScheduleRequestDetails = React.memo(
  ({ scheduleRequestId, requestVersion, locationId, isPaymentsEnabled }: ScheduleRequestDetailsProps) => {
    const alert = useAlert();
    const { t } = useTranslation('scheduleCalendarRequest');
    const size = useBreakpoint();
    const isVerySmallDevice = useMatchMedia({ maxWidth: breakpoints.xsmall.max });
    const isLargeDevice = size === 'large';
    const isSmallDevice = size === 'small' || size === 'xsmall';

    const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();

    const { formatDateAndTime } = useLocationTimezone(locationId);

    const {
      data: scheduleRequestDetails,
      isLoading: isScheduleRequestLoading,
      isError: isScheduleRequestError,
    } = useGetScheduleRequest(scheduleRequestId, requestVersion, locationId);

    const listMappingsQuery = SchedulerV3.Queries.useListMappingsQuery({
      request: {
        locationIds: [locationId],
        providerId: scheduleRequestDetails?.practitionerId ?? '',
        appointmentTypeId: scheduleRequestDetails?.appointmentTypeId ?? '',
        mappingType: MappingType.PROVIDER_APPOINTMENT_TYPE,
        showInactive: true,
      },
      options: {
        enabled:
          requestVersion !== 'v2' &&
          isScheduleV3FlagEnabled &&
          !!scheduleRequestDetails?.practitionerId &&
          !!scheduleRequestDetails?.appointmentTypeId,
      },
    });

    // Fetch appointment type data to get color information
    const { data: appointmentTypesData } = SchedulerV3.Helpers.useGetAppointmentTypesV3Data({
      selectedLocationId: locationId,
      selectedLocationIds: [locationId],
      pageConfig: {
        page: 1,
        limit: 500,
      },
      isEnabled: !!scheduleRequestDetails?.appointmentTypeId,
    });

    const birthDate = getScheduleRequestBirthDate(scheduleRequestDetails?.schedulee?.birthDate);

    const renderRequestTimeListText = useMemo<JSX.Element[] | JSX.Element>(() => {
      if (isEmpty(scheduleRequestDetails?.requestedOpenings)) return <Text>-</Text>;

      // Sort list of dates and times by earliest first
      const sortedOpenings = [...(scheduleRequestDetails?.requestedOpenings || [])].sort((a, b) => {
        if (!a.dateTime && !b.dateTime) return 0;
        if (!a.dateTime) return 1;
        if (!b.dateTime) return -1;
        return new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime();
      });

      const allSameDate = sortedOpenings.every((date) =>
        dayjs(date.dateTime).isSame(dayjs(sortedOpenings[0]?.dateTime), 'date')
      );
      const allSameTz = sortedOpenings.every(
        (date) =>
          formatDateAndTime(date.dateTime, 'z', false) === formatDateAndTime(sortedOpenings[0]?.dateTime, 'z', false)
      );

      // Format the sortedOpenings list of dates and times to not duplicate the date and timezone abbreviation if they are all the same. Example, 'Dec 12 2025, 11:00 AM, 12:15 AM, 2:30 PM EST'. Otherwise keep same format for each datetime. Example, 'Dec 12 2025, 11:00 AM EST, Dec 13 2025, 12:15 AM EST'
      return sortedOpenings.map((opening, i, array) => {
        if (!opening.dateTime) {
          return (
            <Text size='medium' key={`no-date-${i}`}>
              {'-'}
            </Text>
          );
        }
        const isCurrentYear = dayjs(opening.dateTime).isSame(dayjs(), 'year');
        const format = isCurrentYear ? 'MMM D, h:mm A' : 'MMM D YYYY, h:mm A';
        const dateString = formatDateAndTime(opening.dateTime, DATE_DISPLAY_FORMAT, false);

        let textElement: React.ReactNode = null;

        if (allSameDate && allSameTz) {
          const showTzAbbr = i === array.length - 1;
          const fullDate = formatDateAndTime(opening.dateTime, format, showTzAbbr);
          const time = formatDateAndTime(opening.dateTime, 'h:mm A', showTzAbbr);
          textElement = (
            <Text size='medium' key={opening.dateTime} color={isSmallDevice ? 'inherit' : 'primary'}>
              {i === 0 ? fullDate : time}
            </Text>
          );
        }

        textElement = (
          <Text size='medium' key={opening.dateTime} color={isSmallDevice ? 'inherit' : 'primary'}>
            {formatDateAndTime(opening.dateTime, format, true)}
          </Text>
        );

        return (
          <>
            {isSmallDevice ? (
              textElement
            ) : (
              <Link
                key={opening.dateTime}
                to='/schedule/calendar'
                search={{ currentDate: dateString, eventId: scheduleRequestId }}
                css={{ textDecoration: 'none', color: theme.font.colors.primary }}
              >
                {textElement}
              </Link>
            )}
          </>
        );
      });
    }, [formatDateAndTime, scheduleRequestId, scheduleRequestDetails?.requestedOpenings, isSmallDevice]);

    useEffect(() => {
      if (isScheduleRequestError) {
        alert.error(t('Failed to load appointment request details'));
      }
    }, [alert, isScheduleRequestError, t]);

    const scheduleRequestCustomFieldsData = getCustomFieldData(scheduleRequestDetails?.schedulee?.insuranceInfo, [
      'gender',
      'isNewUser',
    ]);

    const getGridColumnConfiguration = (): React.CSSProperties => {
      if (isVerySmallDevice) return { gridTemplateColumns: 'auto' };
      else if (isSmallDevice) return { gridTemplateColumns: 'auto auto' };
      else if (isLargeDevice) return { gridTemplateColumns: '120px 120px 120px' };
      else return { gridTemplateColumns: 'minmax(auto, 120px) minmax(auto, 120px) minmax(auto, 120px)' };
    };

    // Getting appointment Type and provider name from the listMappings API for V3
    const appointmentTypeProviderMapping = listMappingsQuery.data?.mappings?.[0];
    const appointmentTypeName =
      appointmentTypeProviderMapping?.appointmentTypeMetaData?.name || scheduleRequestDetails?.appointmentType || '';

    // Get appointment type color from V3 data
    const appointmentTypeColor =
      appointmentTypesData?.appointmentTypes?.find((type) => type.id === scheduleRequestDetails?.appointmentTypeId)
        ?.details?.clr || '';

    const providerName =
      appointmentTypeProviderMapping?.providerMetaData?.name ||
      scheduleRequestDetails?.requestedOpenings?.[0]?.assets?.providers?.[0] ||
      '';

    return (
      <section css={scheduleRequestContainerStyles}>
        {isScheduleRequestLoading ? (
          <ScheduleRequestDetailsSpinnerLoader />
        ) : (
          <>
            <div css={scheduleRequestGridStyles(getGridColumnConfiguration())}>
              <ScheduleRequestDetailsProperty
                label={t('Appointment Type')}
                value={<AppointmentTypeChip color={appointmentTypeColor} name={appointmentTypeName} />}
              />
              <ScheduleRequestDetailsProperty
                label={t('Provider')}
                value={
                  <Chip
                    color='blue'
                    leftElement={<Avatar size='xxs' name={providerName} />}
                    css={{ border: 0, color: 'inherit' }}
                  >
                    {providerName}
                  </Chip>
                }
              />
              <ScheduleRequestDetailsProperty label={t('Requested Times')} value={renderRequestTimeListText} />
              <ScheduleRequestDetailsProperty label={t('Birthdate')} value={birthDate} />
              <ScheduleRequestDetailsProperty
                label={t('Gender')}
                value={titleCase(scheduleRequestDetails?.schedulee?.gender ?? '') || '-'}
              />
              <ScheduleRequestDetailsProperty
                label={t('Phone Number')}
                value={formatPhoneNumber(scheduleRequestDetails?.schedulee?.phoneNumber ?? '') ?? '-'}
              />
              <ScheduleRequestDetailsProperty
                label={t('Email')}
                value={scheduleRequestDetails?.schedulee?.email ?? '-'}
              />
              <ScheduleRequestDetailsProperty
                label={t('Address')}
                value={getFormattedScheduleRequestAddress(scheduleRequestDetails?.schedulee?.address) ?? '-'}
              />
              <ScheduleRequestDetailsProperty
                label={t('New User')}
                value={scheduleRequestDetails?.schedulee?.existingUser ? t('No') : t('Yes')}
              />
              {isPaymentsEnabled && (
                <ScheduleRequestDetailsProperty
                  label={t('Paid')}
                  value={
                    !!scheduleRequestDetails?.bookingAmount && !!scheduleRequestDetails?.paymentTransactionId ? (
                      <Chip variant='warn' leftElement={<Icon name='dollar-sign-small' />}>
                        {numeric(scheduleRequestDetails?.bookingAmount, { decimals: 2 })}
                      </Chip>
                    ) : (
                      <>-</>
                    )
                  }
                />
              )}
              {!isEmpty(scheduleRequestCustomFieldsData) &&
                Object.entries(scheduleRequestCustomFieldsData).map(([key, value]) => (
                  <ScheduleRequestDetailsProperty key={key} label={key} value={value} />
                ))}
            </div>
            <ScheduleRequestDetailsProperty
              css={{ marginTop: theme.spacing(2) }}
              label={t('Notes')}
              value={scheduleRequestDetails?.note ? scheduleRequestDetails?.note : '-'}
            />
          </>
        )}
      </section>
    );
  }
);

ScheduleRequestDetails.displayName = 'ScheduleRequestDetails';

const scheduleRequestContainerStyles = css({
  borderRadius: theme.borderRadius.small,
  boxShadow: theme.shadows.light,
  background: theme.colors.white,
  padding: theme.spacing(2),
});

const scheduleRequestGridStyles = (gridTemplateColumns: React.CSSProperties) =>
  css({
    display: 'grid',
    gap: theme.spacing(2),
    ...gridTemplateColumns,
  });
